import { blogUrl } from "@repo/fika-blog/src/blogUrl"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import type { PostStat } from "@repo/fika-shared/src/loader/post-stat-schema.js"
import { useNavigate } from "@solidjs/router"
import { sortBy } from "remeda"
import { createEffect, createMemo, Show } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import type { SearchResult } from "@/data/search-index"
import { createSearch } from "@/data/search-sync"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { Card } from "@/designSystem/patterns/card"
import { List } from "@/designSystem/patterns/list"
import { Navigation } from "@/designSystem/patterns/navigation"
import { PostComponent } from "@/designSystem/patterns/post"
import SearchForm from "@/designSystem/patterns/search-form"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/defaultJsonContent"
import {
  createBlogRouter,
  FILTER,
  type Filter,
  SORT,
  type Sort,
} from "@/routes/posts-router"
import { StatsHeader } from "../../designSystem/patterns/stats-header"
import { cardsClass, itemClass, statsHeaderClass } from "./post-list.css"

export function PostList() {
  const { user } = useAppContext()
  const userName = createMemo(() => user.name)
  const navigate = useNavigate()
  const { paths } = createBlogRouter()

  createEffect(() => {
    if (!userName()) {
      navigate(paths().settings().url)
    }
  })

  return (
    <Show when={userName()}>
      <Table />
    </Show>
  )
}

type Result = {
  searchResult?: SearchResult
  post: Post
  postStat: PostStat | null
}

function Table() {
  const { db, user, replicache } = useAppContext()
  const { LL } = useI18nContext()
  const navigate = useNavigate()
  const { paths, listParams } = createBlogRouter()

  const searchResults = createSearch(() => listParams().query, "post")

  const results = createMemo<Result[]>(() => {
    const results = searchResults()

    if (results) {
      return results.flatMap((searchResult, i) => {
        const post = db.post.get(String(searchResult.id))
        if (!post) return []

        return {
          searchResult: {
            order: i,
            highlights: searchResult.highlight,
          },
          post,
          postStat: db.postStat.get(post.id),
        }
      })
    }

    return db.post.filterBy({ latest: true }).map(post => ({
      postStat: db.postStat.get(post.id),
      post,
    }))
  })

  function sorter(result: Result) {
    switch (listParams().sort) {
      case "date":
        return result.post.published_at
      case "views":
        return result.postStat?.recentViews
    }
  }

  function filterByFilter(post: Post, filter: Filter) {
    if (filter === "published") return !!post.published_at
    if (filter === "archived") return !!post.archived_at
    if (filter === "sent") return !!post.sent_at
    if (filter === "draft") return !post.published_at

    return true
  }

  function filterByTag(post: Post, tag: string) {
    if (tag === "all") return true

    return db.tag.findBy({
      target_type: "post",
      target_id: post.id,
      tag,
    })
  }

  const listResults = createMemo(() => {
    const items = results().filter(
      item =>
        filterByFilter(item.post, listParams().filter as Filter) &&
        filterByTag(item.post, listParams().tag),
    )

    return sortBy(
      items,
      [result => result.searchResult?.order ?? 0, "asc"],
      [result => sorter(result) ?? 0, "desc"],
      [result => result.post?.created_at ?? 0, "desc"],
    )
  })

  async function onCreatePost() {
    const id = ulid()
    const title = ""

    await replicache.mutate.createPost({ id, title, json: defaultJsonContent })

    navigate(paths({ filter: "draft" }).post({ id, title }).url)
  }

  const filterValues = createMemo(() => {
    const tagResults = results().filter(item =>
      filterByTag(item.post, listParams().tag),
    )

    return {
      values: Object.values(FILTER).map(filter => ({
        ...filter,
        count: tagResults.filter(item =>
          filterByFilter(item.post, filter.value),
        ).length,
      })),
      value: listParams().filter,
    }
  })

  const tagValues = createMemo(() => {
    const allTags = Array.from(
      new Set(db.tag.filterBy({ target_type: "post" }).map(tag => tag.tag)),
    )
    const filterResults = results().filter(item =>
      filterByFilter(item.post, listParams().filter),
    )
    const values = allTags.flatMap(tag => {
      const count = filterResults.filter(item =>
        filterByTag(item.post, tag),
      ).length
      if (count === 0) return []

      return {
        value: tag,
        count,
      }
    })

    return {
      values: [
        {
          value: "all",
          count: filterResults.length,
        },
        ...values,
      ],
      value: listParams().tag,
    }
  })

  return (
    <MainLayout
      header={<Navigation hue="amber" title="blog" />}
      footer={
        <ButtonDrawer
          hue="amber"
          leftActions={
            <Show when={user.name}>
              {userName => (
                <LinkButton
                  hue="amber"
                  href={`${blogUrl(import.meta.env.VITE_FIKA_PUBLIC_URL, userName())}`}
                  iconName="external-link"
                  kind="darkGhost"
                >
                  visit
                </LinkButton>
              )}
            </Show>
          }
          rightActions={
            <Button hue="amber" onClick={onCreatePost} iconName="pen-tool">
              {LL().common.addPosts()}
            </Button>
          }
        />
      }
    >
      <Box gap="32">
        <ul class={cardsClass}>
          <li class={itemClass}>
            <Card
              hue="amber"
              href={paths().settings().url}
              logo={<Icon name="settings" color="amber600" />}
              iconSize="18"
            >
              <Text color="amber900" fontWeight="500">
                blog settings
              </Text>
            </Card>
          </li>
          <li class={itemClass}>
            <Card
              hue="amber"
              href={paths().stats()}
              logo={<Icon name="bar-chart-big" color="amber600" />}
              iconSize="18"
            >
              <Text color="amber900" fontWeight="500">
                stats
              </Text>
            </Card>
          </li>
        </ul>
        <div class={statsHeaderClass}>
          <StatsHeader
            interval={listParams().interval}
            measure={listParams().measure}
            onChange={change => {
              navigate(paths(change).url)
            }}
          />
        </div>
        <List
          list={listResults()}
          pagination={{
            path: page => paths({ page }).url,
            page: listParams().page,
          }}
          headerOptions={
            <SearchForm
              query={listParams().query}
              onChange={({ query, filter, sort, tag }) => {
                navigate(
                  paths({
                    filter: filter as Filter,
                    sort: sort as Sort,
                    tag,
                    query,
                  }).url,
                )
              }}
              tag={tagValues()}
              filter={filterValues()}
              sort={{
                values: SORT.map(value => ({ value })),
                value: listParams().sort,
              }}
            />
          }
          content={result => (
            <PostComponent
              post={result.post}
              searchResult={result.searchResult}
            />
          )}
          href={result => paths().post(result.post).url}
        />
      </Box>
    </MainLayout>
  )
}
